'use client';
import { useEffect } from 'react';

import { config, type Partner } from '@/configs/partner';
import { useRouter } from '@/i18n/routing';
import { accountAdded, emailAdded } from '@/lib/account/accountSlice';
import { useAppDispatch } from '@/lib/hooks';

import { accountHasContent } from '../utils';
import useSessionData from './useSessionData';

interface Props {
  partner: Partner;
  email: string;
  pathname: string;
}

const useSession = ({ partner, email, pathname }: Props) => {
  const dispatch = useAppDispatch();
  const { push } = useRouter();
  const partnerConfig = config[partner];

  // Check whether or not to fetch & hydrate data
  const shouldVerifySession = !partnerConfig?.publicRoutes?.includes(pathname);
  const enabled =
    !!email &&
    shouldVerifySession &&
    !!partnerConfig.fetchDataRoutes?.includes(pathname);

  // Fetch data
  const { data, isLoading, isError } = useSessionData({
    email,
    partner,
    partnerConfig,
    enabled,
  });

  // Hydrate state & route user
  useEffect(() => {
    if (!data || !enabled) return;

    if (email) dispatch(emailAdded(email));
    if (accountHasContent(data.account)) dispatch(accountAdded(data.account!));

    push(partnerConfig.accountStep);
  }, [email, data, partnerConfig, dispatch, push, enabled]);

  useEffect(() => {
    if (isError) push(partnerConfig.invalidStep);
  }, [isError, partnerConfig, push]);

  return { data, isLoading, isError };
};

export default useSession;
